/**
 * Modèle d'affichage de l'image partagée dérivé d'un `EventRow`.
 *
 * Logique pure et sûre vis-à-vis des champs nullables (`city`, `ends_at`,
 * `price_*`…) : l'image doit rester belle même quand ils manquent.
 */

import { parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { categoryOf } from '@/constants/categories';
import { upperCity } from '@/lib/city';
import type { EventRow, EventKind } from '@/types/database';

// Les horaires des événements sont exprimés en heure de Paris (cf. lib/dates).
// On formate donc la date explicitement dans ce fuseau : le rendu est identique
// quel que soit le fuseau de l'appareil ou de la CI (déterminisme).
const TZ = 'Europe/Paris';

export interface ShareCardData {
  kind: EventKind;
  title: string;
  dateLabel: string;
  cityLabel: string | null;
  categoryLabel: string;
  priceLabel: string | null;
}

function capitalizeFirst(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** Date lisible par un humain : « Samedi 12 septembre », ou « Du 12 au 15 septembre ». */
export function formatShareDate(startsAt: string, endsAt?: string | null): string {
  const start = parseISO(startsAt);
  const fmt = (date: Date, pattern: string) => formatInTimeZone(date, TZ, pattern, { locale: fr });
  const nowYear = formatInTimeZone(new Date(), TZ, 'yyyy');
  const withYear = fmt(start, 'yyyy') !== nowYear;

  if (endsAt) {
    const end = parseISO(endsAt);
    if (fmt(start, 'yyyy-MM-dd') !== fmt(end, 'yyyy-MM-dd')) {
      const sameMonth = fmt(start, 'yyyy-MM') === fmt(end, 'yyyy-MM');
      const left = sameMonth ? fmt(start, 'd') : fmt(start, 'd MMMM');
      const right = fmt(end, withYear ? 'd MMMM yyyy' : 'd MMMM');
      return capitalizeFirst(`du ${left} au ${right}`);
    }
  }

  return capitalizeFirst(fmt(start, withYear ? 'EEEE d MMMM yyyy' : 'EEEE d MMMM'));
}

/** « Gratuit », « 12 € », « 12–20 € », ou null si le tarif est inconnu. */
export function formatSharePrice(event: Pick<EventRow, 'is_free' | 'price_min' | 'price_max'>): string | null {
  if (event.is_free) return 'Gratuit';
  if (event.price_min != null) {
    if (event.price_max != null && event.price_max > event.price_min) {
      return `${event.price_min}–${event.price_max} €`;
    }
    return `${event.price_min} €`;
  }
  return null;
}

export function toShareCardData(event: EventRow): ShareCardData {
  return {
    kind: event.kind,
    title: event.title.trim(),
    dateLabel: formatShareDate(event.starts_at, event.ends_at),
    cityLabel: upperCity(event.city),
    categoryLabel: categoryOf(event.kind).label.toUpperCase(),
    priceLabel: formatSharePrice(event),
  };
}

/** Légende courte accompagnant l'image : titre, date, ville, lien vers l'app. */
export function buildShareCaption(data: ShareCardData, url: string): string {
  const dateCity = data.cityLabel ? `${data.dateLabel} · ${data.cityLabel}` : data.dateLabel;
  return `${data.title}\n${dateCity}\n${url}`;
}
